/*
 * Shared helpers used by both lib/fields.js (A/B entities) and
 * product/fields-other.js (C entities) — extracted to avoid an import cycle.
 */
import { h } from 'preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

export const COLOR_PICKER_LIST = [
    // Primary web colors
    ['#0d6efd', 'Primary'],
    ['#6c757d', 'Secondary'],
    ['#198754', 'Success'],
    ['#0dcaf0', 'Info'],
    ['#ffc107', 'Warning'],
    ['#dc3545', 'Danger'],
    ['#f8f9fa', 'Light'],
    ['#212529', 'Dark'],

    // Complete list of web colors
    ['#F08080', 'Light Coral'],
    ['#FA8072', 'Salmon'],
    ['#E9967A', 'Dark Salmon'],
    ['#FF6347', 'Tomato'],
    ['#FF4500', 'Orange Red'],
    ['#FF7F50', 'Coral'],
    ['#FFA500', 'Orange'],
    ['#FF8C00', 'Dark Orange'],
    ['#F4A460', 'Sandy Brown'],
    ['#CD853F', 'Peru'],
    ['#DEB887', 'Burlywood'],
    ['#FFD700', 'Gold'],
    ['#DAA520', 'Goldenrod'],
    ['#FFE4B5', 'Moccasin'],
    ['#FFDEAD', 'Navajo White'],
    ['#FFF8DC', 'Cornsilk'],
    ['#FFFACD', 'Lemon Chiffon'],
    ['#F0E68C', 'Khaki'],
    ['#ADFF2F', 'Green Yellow'],
    ['#7FFF00', 'Chartreuse'],
    ['#90EE90', 'Light Green'],
    ['#98FB98', 'Pale Green'],
    ['#32CD32', 'Lime Green'],
    ['#228B22', 'Forest Green'],
    ['#2E8B57', 'Sea Green'],
    ['#00FA9A', 'Medium Spring Green'],
    ['#66CDAA', 'Medium Aquamarine'],
    ['#7FFFD4', 'Aquamarine'],
    ['#40E0D0', 'Turquoise'],
    ['#AFEEEE', 'Pale Turquoise'],
    ['#00CED1', 'Dark Turquoise'],
    ['#5F9EA0', 'Cadet Blue'],
    ['#B0E0E6', 'Powder Blue'],
    ['#87CEFA', 'Light Sky Blue'],
    ['#87CEEB', 'Sky Blue'],
    ['#1E90FF', 'Dodger Blue'],
    ['#4169E1', 'Royal Blue'],
    ['#6495ED', 'Cornflower Blue'],
    ['#B0C4DE', 'Light Steel Blue'],
    ['#6A5ACD', 'Slate Blue'],
    ['#7B68EE', 'Medium Slate Blue'],
    ['#8A2BE2', 'Blue Violet'],
    ['#9370DB', 'Medium Purple'],
    ['#BA55D3', 'Medium Orchid'],
    ['#EE82EE', 'Violet'],
    ['#DA70D6', 'Orchid'],
    ['#DDA0DD', 'Plum'],
    ['#DB7093', 'Pale Violet Red'],
    ['#FF69B4', 'Hot Pink'],
    ['#D8BFD8', 'Thistle'],
];

// Gradient backgrounds for feed posts. Value format: '<css background-image>;<text hex>'.
// The css part may contain commas (multi-stop and layered gradients) but never ';'.
// Shared by the composer swatch picker (via BACKGROUND_PICKER_LIST) and the
// admin/full edit form's backgroundImage select (product/fields-other.js).
export const GRADIENT_PICKER_LIST = [
    // Signature backgrounds — the first five lead the composer's one-line view.
    ['linear-gradient(160deg, #0b1a2b 0%, #103a3f 35%, #1d976c 60%, #7b4397 100%);#ffffff', 'Nordlys'],
    ['linear-gradient(180deg, #355c7d 0%, #c06c84 55%, #f8b195 100%);#ffffff', 'Midnattssol'],
    [
        'radial-gradient(circle at 18% 15%, rgba(126,249,255,0.92) 0%, rgba(126,249,255,0) 36%), '
        + 'radial-gradient(circle at 84% 12%, rgba(213,111,255,0.82) 0%, rgba(213,111,255,0) 42%), '
        + 'linear-gradient(145deg, #061a33 0%, #12506f 48%, #4c2d7f 100%);#ffffff',
        'AuroraGlass',
    ],
    [
        'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0) 34%), '
        + 'radial-gradient(circle at 82% 88%, rgba(217,167,199,0.82) 0%, rgba(217,167,199,0) 44%), '
        + 'linear-gradient(145deg, #fce4ec 0%, #e8c5df 48%, #c9b6e4 100%);#29192d',
        'RoseQuartz',
    ],
    [
        'radial-gradient(circle at 76% 18%, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0) 32%), '
        + 'radial-gradient(circle at 12% 88%, rgba(87,213,196,0.72) 0%, rgba(87,213,196,0) 42%), '
        + 'linear-gradient(140deg, #b8e8f0 0%, #71c6d3 48%, #7295d8 100%);#102a43',
        'OceanPearl',
    ],
    [
        'radial-gradient(circle at 14% 18%, rgba(255,82,182,0.68) 0%, rgba(255,82,182,0) 38%), '
        + 'radial-gradient(circle at 88% 72%, rgba(68,217,255,0.65) 0%, rgba(68,217,255,0) 42%), '
        + 'linear-gradient(135deg, #170d35 0%, #4d1b68 48%, #102b58 100%);#ffffff',
        'VelvetAurora',
    ],
    [
        'radial-gradient(circle at 50% -8%, rgba(200,255,243,0.92) 0%, rgba(200,255,243,0) 42%), '
        + 'radial-gradient(circle at 8% 82%, rgba(72,187,255,0.72) 0%, rgba(72,187,255,0) 38%), '
        + 'linear-gradient(155deg, #0a2338 0%, #176471 52%, #263f74 100%);#ffffff',
        'ArcticHalo',
    ],
    [
        'radial-gradient(circle at 18% 24%, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0) 34%), '
        + 'radial-gradient(circle at 86% 74%, rgba(255,182,213,0.62) 0%, rgba(255,182,213,0) 42%), '
        + 'linear-gradient(130deg, #dff6ff 0%, #d8d0f2 48%, #b7ebde 100%);#262238',
        'OpalCloud',
    ],
    [
        'radial-gradient(circle at 72% 8%, rgba(255,229,144,0.64) 0%, rgba(255,229,144,0) 35%), '
        + 'radial-gradient(circle at 14% 88%, rgba(58,178,137,0.54) 0%, rgba(58,178,137,0) 40%), '
        + 'linear-gradient(145deg, #0e2d2a 0%, #1b5444 50%, #15352f 100%);#ffffff',
        'ForestCanopy',
    ],
    [
        'radial-gradient(circle at 24% 12%, rgba(255,255,255,0.76) 0%, rgba(255,255,255,0) 30%), '
        + 'linear-gradient(120deg, rgba(255,238,177,0) 25%, rgba(255,238,177,0.82) 48%, '
        + 'rgba(255,238,177,0) 70%), linear-gradient(145deg, #aa6f28 0%, #f1c86e 52%, #8e5520 100%);#2a1a0b',
        'GoldenSilk',
    ],
    [
        'radial-gradient(circle at 18% 78%, rgba(0,246,255,0.78) 0%, rgba(0,246,255,0) 38%), '
        + 'radial-gradient(circle at 82% 18%, rgba(255,52,179,0.82) 0%, rgba(255,52,179,0) 40%), '
        + 'linear-gradient(135deg, #17133c 0%, #49217a 48%, #172f55 100%);#ffffff',
        'ElectricBloom',
    ],
    [
        'radial-gradient(circle at 72% 12%, rgba(136,194,255,0.55) 0%, rgba(136,194,255,0) 38%), '
        + 'linear-gradient(155deg, #071426 0%, #183b63 48%, #526a92 100%);#ffffff',
        'BlueHour',
    ],
    [
        'radial-gradient(circle at 20% 18%, rgba(255,219,187,0.72) 0%, rgba(255,219,187,0) 38%), '
        + 'radial-gradient(circle at 84% 82%, rgba(69,208,190,0.68) 0%, rgba(69,208,190,0) 44%), '
        + 'linear-gradient(140deg, #ff9f92 0%, #edb5aa 46%, #7dd8d0 100%);#142f35',
        'CoralLagoon',
    ],
    [
        'radial-gradient(circle at 78% 16%, rgba(255,240,247,0.88) 0%, rgba(255,240,247,0) 36%), '
        + 'linear-gradient(135deg, #d8c7ef 0%, #c8c9f1 46%, #f0c7d8 100%);#2d2340',
        'LavenderDawn',
    ],
    [
        'radial-gradient(circle at 28% 18%, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0) 28%), '
        + 'radial-gradient(circle at 80% 82%, rgba(130,165,193,0.62) 0%, rgba(130,165,193,0) 42%), '
        + 'linear-gradient(145deg, #dce6ec 0%, #aabecb 50%, #778da2 100%);#172438',
        'Moonstone',
    ],
    [
        'radial-gradient(circle at 80% 12%, rgba(79,255,174,0.55) 0%, rgba(79,255,174,0) 36%), '
        + 'linear-gradient(145deg, #071f1a 0%, #0b4f3b 46%, #12382f 100%);#ffffff',
        'EmeraldVelvet',
    ],
    [
        'radial-gradient(circle at 18% 16%, rgba(255,224,121,0.88) 0%, rgba(255,224,121,0) 34%), '
        + 'radial-gradient(circle at 82% 78%, rgba(255,55,88,0.72) 0%, rgba(255,55,88,0) 42%), '
        + 'linear-gradient(135deg, #6f1735 0%, #d34835 48%, #ef8b3a 100%);#ffffff',
        'SolarFlare',
    ],
    [
        'radial-gradient(circle at 74% 18%, rgba(143,105,255,0.62) 0%, rgba(143,105,255,0) 36%), '
        + 'radial-gradient(circle at 16% 82%, rgba(38,190,207,0.42) 0%, rgba(38,190,207,0) 40%), '
        + 'linear-gradient(145deg, #090d26 0%, #241342 52%, #151b3f 100%);#ffffff',
        'MidnightIris',
    ],
    [
        'radial-gradient(circle at 20% 16%, rgba(255,255,255,0.76) 0%, rgba(255,255,255,0) 32%), '
        + 'linear-gradient(135deg, #ffd6c2 0%, #f6a9b8 48%, #dc9cce 100%);#3b2431',
        'PeachSorbet',
    ],
    [
        'radial-gradient(circle at 72% 12%, rgba(220,255,246,0.68) 0%, rgba(220,255,246,0) 34%), '
        + 'linear-gradient(150deg, #123d4b 0%, #247b76 48%, #4a6f95 100%);#ffffff',
        'AlpineLake',
    ],
    [
        'radial-gradient(circle at 24% 16%, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0) 32%), '
        + 'radial-gradient(circle at 82% 82%, rgba(224,174,112,0.42) 0%, rgba(224,174,112,0) 40%), '
        + 'linear-gradient(140deg, #f6ead8 0%, #e8cfad 50%, #cfaa7f 100%);#2c2118',
        'ChampagneGlow',
    ],
    [
        'radial-gradient(circle at 16% 22%, rgba(255,61,151,0.68) 0%, rgba(255,61,151,0) 38%), '
        + 'radial-gradient(circle at 84% 76%, rgba(97,93,255,0.72) 0%, rgba(97,93,255,0) 42%), '
        + 'linear-gradient(140deg, #220d34 0%, #681942 48%, #1a2456 100%);#ffffff',
        'CosmicRose',
    ],
    [
        'radial-gradient(circle at 18% 16%, rgba(255,226,134,0.72) 0%, rgba(255,226,134,0) 36%), '
        + 'radial-gradient(circle at 84% 82%, rgba(33,237,204,0.68) 0%, rgba(33,237,204,0) 42%), '
        + 'linear-gradient(140deg, #14504e 0%, #168f87 48%, #315d8d 100%);#ffffff',
        'TropicalGlass',
    ],
    [
        'radial-gradient(circle at 78% 20%, rgba(238,190,92,0.72) 0%, rgba(238,190,92,0) 30%), '
        + 'linear-gradient(125deg, rgba(255,218,122,0) 30%, rgba(255,218,122,0.52) 48%, '
        + 'rgba(255,218,122,0) 66%), linear-gradient(145deg, #10151e 0%, #292a32 52%, #151921 100%);#ffffff',
        'InkAndGold',
    ],
    [
        'radial-gradient(circle at 22% 16%, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0) 32%), '
        + 'radial-gradient(circle at 84% 80%, rgba(182,137,255,0.52) 0%, rgba(182,137,255,0) 40%), '
        + 'linear-gradient(140deg, #c8f4f1 0%, #9ed6e5 48%, #b9b8ea 100%);#143047',
        'GlacierBloom',
    ],
    [
        'radial-gradient(circle at 18% 22%, rgba(255,255,255,0.86) 0%, rgba(255,255,255,0) 1.5%), '
        + 'radial-gradient(circle at 72% 18%, rgba(194,220,255,0.72) 0%, rgba(194,220,255,0) 2%), '
        + 'radial-gradient(circle at 84% 76%, rgba(255,192,231,0.66) 0%, rgba(255,192,231,0) 2%), '
        + 'radial-gradient(circle at 32% 78%, rgba(162,228,255,0.7) 0%, rgba(162,228,255,0) 1.5%), '
        + 'linear-gradient(145deg, #060c21 0%, #18264b 52%, #27193f 100%);#ffffff',
        'Starlight',
    ],

    // Rich classic gradients and metallics.
    ['linear-gradient(135deg, #b8860b 0%, #ffd700 40%, #fff3b0 55%, #ffd700 70%, #b8860b 100%);#000000', 'Gull'],
    ['linear-gradient(135deg, #757f9a 0%, #d7dde8 50%, #f5f7fa 65%, #aeb6c3 100%);#000000', 'Sølv'],
    ['linear-gradient(135deg, #6d3b12 0%, #b87333 45%, #e8b57e 60%, #8c5a2b 100%);#ffffff', 'Bronse'],
    ['linear-gradient(120deg, #0f0c29 0%, #302b63 50%, #24243e 100%);#00ffcc', 'NeonNights'],
    ['linear-gradient(180deg, #241734 0%, #9a1f6a 60%, #ff6c11 100%);#ffffff', 'Synthwave'],
    ['radial-gradient(circle at 30% 30%, #3b2667 0%, #16123f 45%, #050014 100%);#ffffff', 'DeepSpace'],
    ['radial-gradient(circle at 50% 40%, #4a5568 0%, #1a202c 70%, #0d1117 100%);#ffffff', 'Spotlight'],
    ['radial-gradient(circle at 50% 35%, #ffd194 0%, #d1913c 60%, #70361c 100%);#ffffff', 'GyllenTime'],
    ['linear-gradient(135deg, #83a4d4 0%, #b6fbff 100%);#000000', 'Vinterkveld'],
    ['linear-gradient(45deg, #a43931 0%, #ffb347 100%);#ffffff', 'Høstkveld'],
    ['linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);#000000', 'PeachFuzz'],
    ['linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%);#000000', 'MorningMist'],
    ['linear-gradient(135deg, #fbc7d4 0%, #9796f0 100%);#000000', 'LavenderHaze'],
    ['linear-gradient(135deg, #d9f7be 0%, #5eead4 100%);#000000', 'MintDream'],
    ['linear-gradient(30deg, #2b5876 0%, #4e4376 100%);#ffffff', 'OceanSunrise'],
    ['linear-gradient(45deg, #200122 0%, #6f0000 100%);#ffffff', 'Ember'],
    ['linear-gradient(200deg, #1a2980 0%, #26d0ce 100%);#ffffff', 'NorthSea'],
    ['linear-gradient(45deg, #c31432 0%, #240b36 100%);#ffffff', 'Bringebær'],
    ['linear-gradient(45deg, rgba(255,0,150,0.55) 0%, rgba(255,0,150,0) 60%), linear-gradient(135deg, #7f7fd5 0%, #86a8e7 50%, #91eae4 100%);#000000', 'CandyMesh'],

    ['linear-gradient(45deg, #f093fb 0%, #f5576c 100%);#ffffff', 'SunsetGlow'],
    ['linear-gradient(45deg, #f6d365 0%, #fda085 100%);#ffffff', 'WarmSunrise'],
    ['linear-gradient(45deg, #5ee7df 0%, #b490ca 100%);#ffffff', 'AquaMarine'],
    ['linear-gradient(45deg, #7579ff 0%, #b224ef 100%);#ffffff', 'SteelPurple'],
    ['linear-gradient(45deg, #3ca55c 0%, #b5ac49 100%);#ffffff', 'Seabreeze'],
    ['linear-gradient(45deg, #ee9ca7 0%, #ffdde1 100%);#ffffff', 'OrchidHeaven'],
    ['linear-gradient(45deg, #141e30 0%, #243b55 100%);#ffffff', 'EveningNight'],

    // New hard-edged (abrupt stop) gradients
    ['linear-gradient(15deg, #f12711 0%, #f5af19 100%);#ffffff', 'TangerineBlaze'],
    ['linear-gradient(120deg, #84fab0 0%, #8fd3f4 100%);#000000', 'MintSky'],
    ['linear-gradient(200deg, #fceabb 0%, #f8b500 50%, #c21500 50%, #c21500 100%);#ffffff', 'DesertEdge'],
    ['linear-gradient(70deg, #00d2ff 0%, #3a7bd5 100%);#ffffff', 'BlueShift'],
    ['linear-gradient(60deg, #dd3e54 0%, #6be585 100%);#ffffff', 'WatermelonFlash'],
    ['linear-gradient(35deg, #bdc3c7 0%, #2c3e50 100%);#ffffff', 'SteelFade'],
    ['linear-gradient(90deg, #009fff 0%, #ec2f4b 100%);#ffffff', 'FireAndIce'],
    ['linear-gradient(135deg, #ef32d9 0%, #89fffd 100%);#000000', 'CottonCandy'],
    ['linear-gradient(25deg, #fffc00 0%, #ffffff 25%, #00fffc 25%, #00fffc 75%, #ff00b4 75%, #ff00b4 100%);#000000', 'TechnoStripes'],
    ['linear-gradient(45deg, #ff9966 0%, #ff5e62 100%);#ffffff', 'PeachMelba'],
    ['linear-gradient(145deg, #667eea 0%, #764ba2 40%, #ffafd2 40%, #ffafd2 80%, #50c9c3 80%, #50c9c3 100%);#ffffff', 'LavenderPop'],
    ['linear-gradient(0deg, #3a1c71 0%, #d76d77 50%, #ffaf7b 100%);#ffffff', 'WarmTrinity'],
    ['linear-gradient(45deg, #f5515f 0%, #9f041b 100%);#ffffff', 'CrimsonNight'],
    ['linear-gradient(90deg, #8e2de2 0%, #4a00e0 25%, #4a00e0 75%, #8e2de2 75%, #8e2de2 100%);#ffffff', 'RoyalViolet'],
    ['linear-gradient(135deg, #fffcdc 0%, #d9a7c7 100%);#000000', 'LilyFields'],
    ['linear-gradient(75deg, #2193b0 0%, #6dd5ed 30%, #cc2b5e 30%, #cc2b5e 70%, #753a88 70%, #753a88 100%);#ffffff', 'DuskStreaks'],
    ['linear-gradient(135deg, #11998e 0%, #38ef7d 100%);#000000', 'EmeraldLane'],
    ['linear-gradient(110deg, #ee0979 0%, #ff6a00 50%, #00c9ff 50%, #00c9ff 100%);#ffffff', 'FlamingSunset'],
    ['linear-gradient(180deg, #09203f 0%, #537895 100%);#ffffff', 'DeepOcean'],
    ['linear-gradient(45deg, #fbda61 0%, #ff5acd 100%);#000000', 'PinkLemonade'],

    // Nordic flags. Layered gradients preserve the characteristic offset cross.
    [
        'linear-gradient(180deg, transparent 46%, #00205b 46%, #00205b 54%, transparent 54%), '
        + 'linear-gradient(90deg, transparent 33%, #00205b 33%, #00205b 38%, transparent 38%), '
        + 'linear-gradient(90deg, transparent 29%, #ffffff 29%, #ffffff 42%, transparent 42%), '
        + 'linear-gradient(180deg, #ba0c2f 0%, #ba0c2f 39%, #ffffff 39%, #ffffff 61%, '
        + '#ba0c2f 61%, #ba0c2f 100%);#ffffff',
        'NorwayFlag',
    ],
    [
        'linear-gradient(90deg, transparent 30%, #fecc02 30%, #fecc02 40%, transparent 40%), '
        + 'linear-gradient(180deg, #006aa7 0%, #006aa7 42%, #fecc02 42%, #fecc02 58%, '
        + '#006aa7 58%, #006aa7 100%);#000000',
        'SwedenFlag',
    ],
    [
        'linear-gradient(90deg, transparent 30%, #ffffff 30%, #ffffff 40%, transparent 40%), '
        + 'linear-gradient(180deg, #c8102e 0%, #c8102e 42%, #ffffff 42%, #ffffff 58%, '
        + '#c8102e 58%, #c8102e 100%);#000000',
        'DenmarkFlag',
    ],
    [
        'linear-gradient(90deg, transparent 30%, #003580 30%, #003580 40%, transparent 40%), '
        + 'linear-gradient(180deg, #ffffff 0%, #ffffff 42%, #003580 42%, #003580 58%, '
        + '#ffffff 58%, #ffffff 100%);#ffffff',
        'FinlandFlag',
    ],
    [
        'linear-gradient(180deg, transparent 46%, #d72828 46%, #d72828 54%, transparent 54%), '
        + 'linear-gradient(90deg, transparent 33%, #d72828 33%, #d72828 38%, transparent 38%), '
        + 'linear-gradient(90deg, transparent 29%, #ffffff 29%, #ffffff 42%, transparent 42%), '
        + 'linear-gradient(180deg, #02529c 0%, #02529c 39%, #ffffff 39%, #ffffff 61%, '
        + '#02529c 61%, #02529c 100%);#ffffff',
        'IcelandFlag',
    ],
    [
        'linear-gradient(180deg, transparent 46%, #ed2939 46%, #ed2939 54%, transparent 54%), '
        + 'linear-gradient(90deg, transparent 33%, #ed2939 33%, #ed2939 38%, transparent 38%), '
        + 'linear-gradient(90deg, transparent 29%, #0065bd 29%, #0065bd 42%, transparent 42%), '
        + 'linear-gradient(180deg, #ffffff 0%, #ffffff 39%, #0065bd 39%, #0065bd 61%, '
        + '#ffffff 61%, #ffffff 100%);#ffffff',
        'FaroeIslandsFlag',
    ],

    // France: blue - white - red (vertical stripes)
    ['linear-gradient(90deg, #0055A4 0%, #0055A4 33%, #FFFFFF 33%, #FFFFFF 67%, #EF4135 67%, #EF4135 100%);#000000', 'FranceFlag'],
    // Germany: black - red - gold (horizontal stripes)
    ['linear-gradient(180deg, #000000 0%, #000000 33%, #DD0000 33%, #DD0000 67%, #FFCE00 67%, #FFCE00 100%);#ffffff', 'GermanyFlag'],
    // Italy: green - white - red (vertical stripes)
    ['linear-gradient(90deg, #008C45 0%, #008C45 33%, #FFFFFF 33%, #FFFFFF 67%, #CD212A 67%, #CD212A 100%);#000000', 'ItalyFlag'],
    // Netherlands: red - white - blue (horizontal stripes)
    ['linear-gradient(180deg, #AE1C28 0%, #AE1C28 33%, #FFFFFF 33%, #FFFFFF 67%, #21468B 67%, #21468B 100%);#000000', 'NetherlandsFlag'],
    // Ukraine: blue on top, yellow on bottom (horizontal stripes)
    ['linear-gradient(180deg, #0057B7 0%, #0057B7 50%, #FFDD00 50%, #FFDD00 100%);#000000', 'UkraineFlag'],
    // Belgium: black - yellow - red (vertical stripes)
    ['linear-gradient(90deg, #000000 0%, #000000 33%, #FDDA24 33%, #FDDA24 67%, #EF3340 67%, #EF3340 100%);#ffffff', 'BelgiumFlag'],
    // Ireland: green - white - orange (vertical stripes)
    ['linear-gradient(90deg, #169B62 0%, #169B62 33%, #FFFFFF 33%, #FFFFFF 67%, #FF883E 67%, #FF883E 100%);#000000', 'IrelandFlag'],
    // Romania: blue - yellow - red (vertical stripes)
    ['linear-gradient(90deg, #002B7F 0%, #002B7F 33%, #FCD116 33%, #FCD116 67%, #CE1126 67%, #CE1126 100%);#000000', 'RomaniaFlag'],
    // Estonia: blue - black - white (horizontal stripes)
    ['linear-gradient(180deg, #0072CE 0%, #0072CE 33%, #000000 33%, #000000 67%, #FFFFFF 67%, #FFFFFF 100%);#000000', 'EstoniaFlag'],

    // Great Britain (Union Jack is complex; just color blocks)
    ['linear-gradient(180deg, #012169 0%, #012169 40%, #FFFFFF 40%, #FFFFFF 50%, #C8102E 50%, #C8102E 60%, #FFFFFF 60%, #FFFFFF 70%, #012169 70%, #012169 100%);#000000', 'GreatBritainFlag'],
    // Scotland (Diagonal white cross on blue; simplified as stripes)
    ['linear-gradient(180deg, #0065BF 0%, #0065BF 50%, #FFFFFF 50%, #FFFFFF 100%);#000000', 'ScotlandFlag'],
    // Wales (Green, white, red dragon in reality; just approximate)
    ['linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, #D00C33 50%, #D00C33 65%, #00A04D 65%, #00A04D 100%);#000000', 'WalesFlag'],
    // Northern Ireland (White field with red cross + emblem; simplified stripes)
    ['linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 60%, #CF142B 60%, #CF142B 70%, #FFFFFF 70%, #FFFFFF 100%);#000000', 'NorthernIrelandFlag'],
];

// Composer swatch picker list: gradients first (showpieces on the one-line view),
// then the solid colors. New array — never mutate the source lists.
export const BACKGROUND_PICKER_LIST = [...GRADIENT_PICKER_LIST, ...COLOR_PICKER_LIST];

// A picker value is either a bare hex (optionally ',fg' — legacy) or a CSS
// gradient stored verbatim with its foreground after ';' (the GRADIENT_PICKER_LIST
// format above).
export function isGradientValue(value) {
    return typeof value === 'string' && value.includes('gradient(');
}

export function splitBackgroundValue(value = '') {
    const idx = value.indexOf(';');
    if (idx === -1) {
        return { css: value, fg: undefined };
    }
    return { css: value.slice(0, idx), fg: value.slice(idx + 1) || undefined };
}

export function findInvalidEmailChars(email) {
    // A basic allowed character set for illustration:
    //   - alphanumerics
    //   - !#$%&'*+/=?^_`{|}~ (common RFC5322 punctuation)
    //   - @ and .
    // (This is not exhaustive of all possible valid emails, but typical.)
    const allowedRegex = /^[A-Za-z0-9!#$%&'*+\-/=?^_`{|}~.@]+$/;

    const invalids = [];
    // Check each character individually
    for (let i = 0; i < email.length; i++) {
        const c = email[i];
        // If this single character fails the allowed pattern, log it
        if (!c.match(/^[A-Za-z0-9!#$%&'*+\-/=?^_`{|}~.@]$/)) {
            invalids.push({ char: c, index: i + 1 });
        }
    }
    return invalids;
}

export const apiServer = util.getApiServer();

export const spiderPage = async (url) => {
    const response = await util.fetchApi(`/api/spider/${encodeURIComponent(url)}`, {}, this);
    switch (response.status) {
        case 200:
            return {
                ...response.data,
            }
            break;
        default:
            return response;
    }
}

export const getSpiderUrl = async (obj, updateValue, foundUrl) => {
    console.log('Found URL to spider:', foundUrl);
    // 3) spider the URL
    const response = await spiderPage(foundUrl);
    if (response) {
        console.log('Spidered response:', response);
        // Remove url from ingress
        const updatedIngress = obj.ingress.replace(foundUrl, '').trim();
        updateValue({
            name: 'ingress',
            value: updatedIngress,
        });
        updateValue({
            name: 'url',
            value: foundUrl,
        });
        updateValue({
            name: 'urlDomain',
            value: response.domain,
        });
        updateValue({
            name: 'urlBaseUrl',
            value: response.baseUrl,
        });
        updateValue({
            name: 'urlTitle',
            value: response.title,
        });
        updateValue({
            name: 'urlDescription',
            value: response.description,
        });
        updateValue({
            name: 'urlImageUrl',
            value: response.image || response.icon,
        });
        updateValue({
            name: 'urlBrand',
            value: response.product?.brand?.name,
        });
        updateValue({
            name: 'urlCategory',
            value: response.product?.category,
        });
        updateValue({
            name: 'urlSku',
            value: response.product?.sku,
        });
        updateValue({
            name: 'urlMpn',
            value: response.product?.mpn,
        });
        updateValue({
            name: 'urlGtin13',
            value: response.product?.gtin13,
        });
        updateValue({
            name: 'urlPrice',
            value: response.product?.offers?.price,
        });
        updateValue({
            name: 'urlRating',
            value: response.product?.aggregateRating?.ratingValue,
        });
        updateValue({
            name: 'urlRatingCount',
            value: response.product?.aggregateRating?.reviewCount,
        });
    }
}



export const brregLookup = async (orgNumber) => {
    const response = await util.fetchApi(`/api/brreg/${orgNumber}`, { method: 'GET' }, {});
    if (response && response.status === 200) {
        return response.data;
    }
    if (response && response.status === 404) {
        return { __notFound: true };
    }
    return null;
};

export const addTagsIfMissing = (obj, updateValue, tags) => {
    const existingTags = obj.tags || [];
    // Tags has format: [ { name: 'tag1' }, { name: 'tag2' } ]
    const newTags = tags.filter(tag => {
        return !existingTags.some(existingTag => existingTag.name === tag);
    });
    if (newTags.length > 0) {
        updateValue({
            name: 'tags',
            value: [...existingTags, ...newTags.map(tag => ({ name: tag }))],
        });
    }
};

export const postnummerLookup = async (postnummer) => {
    const response = await util.fetchApi(`/api/postnummer/${postnummer}`, { method: 'GET' }, {});
    switch (response.status) {
        case 200:
            console.log('postnummerLookup', response.data);
            return response.data;
    }
}

// --- Date / datetime field shortcuts (I dag / I morgen / +1 uke / Nå) ---

const pad2 = n => String(n).padStart(2, '0');

// Local midnight for the day a shortcut targets. 'now' targets today.
export const dateShortcutTarget = (shortcut, now = new Date()) => {
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (shortcut === 'tomorrow') {
        day.setDate(day.getDate() + 1);
    } else if (shortcut === 'plusWeek') {
        day.setDate(day.getDate() + 7);
    }
    return day;
};

// Day-granularity range check. min/max accept 'today', ISO strings or Date
// objects; invalid or missing bounds are ignored.
export const isShortcutDayInRange = (day, min, max, now = new Date()) => {
    const resolveDay = value => {
        if (!value) {
            return null;
        }
        const date = value === 'today' ? now : new Date(value);
        if (isNaN(date)) {
            return null;
        }
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    };
    const minDay = resolveDay(min);
    const maxDay = resolveDay(max);
    if (minDay && day < minDay) {
        return false;
    }
    if (maxDay && day > maxDay) {
        return false;
    }
    return true;
};

// For end-date fields ('endDate', 'end', 'registrationEndDate', ...) derive
// the matching from-field name ('startDate', 'start', 'registrationStartDate').
// Returns null when the name is not an end-date field.
export const dateFromFieldName = (name = '') => {
    if (/^end/.test(name)) {
        return name.replace(/^end/, 'start');
    }
    if (name.includes('End')) {
        return name.replace('End', 'Start');
    }
    return null;
};

// Value for a datetime-local input set to the from-field's value plus N hours
// ('YYYY-MM-DDTHH:mm'). Returns null when fromValue is missing or invalid.
export const computeFromPlusHoursValue = (fromValue, hours = 2) => {
    if (!fromValue) {
        return null;
    }
    const from = new Date(fromValue);
    if (isNaN(from)) {
        return null;
    }
    from.setHours(from.getHours() + hours);
    return `${from.getFullYear()}-${pad2(from.getMonth() + 1)}-${pad2(from.getDate())}T${pad2(from.getHours())}:${pad2(from.getMinutes())}`;
};

// Value for a datetime-local input ('YYYY-MM-DDTHH:mm'). Day shortcuts keep
// the time already entered in currentValue; 'now' (and empty fields) use the
// current clock time.
export const computeDateShortcutValue = (shortcut, currentValue, now = new Date()) => {
    const target = dateShortcutTarget(shortcut, now);
    let hours = pad2(now.getHours());
    let minutes = pad2(now.getMinutes());
    if (shortcut !== 'now') {
        const match = typeof currentValue === 'string' ? currentValue.match(/T(\d{2}):(\d{2})/) : null;
        if (match) {
            hours = match[1];
            minutes = match[2];
        }
    }
    return `${target.getFullYear()}-${pad2(target.getMonth() + 1)}-${pad2(target.getDate())}T${hours}:${minutes}`;
};

// Shortcut button row for datetime-local fields, rendered via the field's
// showFieldInfo hook (injected centrally in Fields.get). Writes the value by
// dispatching an input event on the real input element — same pattern as
// setUsefulValue in components/form/input.js — so the normal pipeline runs:
// validation, debounced save, admin/new-object handling and field callbacks.
export const renderDateTimeShortcuts = ({ obj = {}, field, fieldDef = {} }) => {
    const now = new Date();
    const min = fieldDef.minFunc ? fieldDef.minFunc(obj) : fieldDef.min;
    const max = fieldDef.maxFunc ? fieldDef.maxFunc(obj) : fieldDef.max;
    const shortcuts = [
        { key: 'today', label: <Text id='input.date-shortcut-today'>Today</Text> },
        { key: 'tomorrow', label: <Text id='input.date-shortcut-tomorrow'>Tomorrow</Text> },
        { key: 'plusWeek', label: <Text id='input.date-shortcut-plus-week'>+1 week</Text> },
        { key: 'now', label: <Text id='input.date-shortcut-now'>Now</Text> },
    ].filter(shortcut => isShortcutDayInRange(dateShortcutTarget(shortcut.key, now), min, max, now));

    // End-date fields get an extra shortcut: from-field's value plus 2 hours.
    const fromField = dateFromFieldName(field);
    if (fromField) {
        const fromPlusValue = computeFromPlusHoursValue(util.getNestedValue(obj, fromField));
        if (fromPlusValue) {
            const target = new Date(fromPlusValue);
            const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate());
            if (isShortcutDayInRange(targetDay, min, max, now)) {
                shortcuts.push({ key: 'fromPlus2h', label: <Text id='input.date-shortcut-from-plus2h'>Start +2h</Text> });
            }
        }
    }

    if (shortcuts.length === 0) {
        return null;
    }

    const onClick = e => {
        e.preventDefault();
        e.stopPropagation();
        const { shortcut } = e.target.closest('button').dataset;
        // Same id scheme input.js uses: `${object.id || ''}${field}Input`.
        const inputEl = document.getElementById(`${obj.id || ''}${field}Input`);
        if (!inputEl) {
            return;
        }
        let value;
        if (shortcut === 'fromPlus2h') {
            // Prefer the from-field's live input value (covers unsaved edits),
            // fall back to the store object.
            const fromInput = document.getElementById(`${obj.id || ''}${fromField}Input`);
            value = computeFromPlusHoursValue((fromInput && fromInput.value) || util.getNestedValue(obj, fromField));
        } else {
            value = computeDateShortcutValue(shortcut, inputEl.value);
        }
        if (!value) {
            return;
        }
        inputEl.value = value;
        inputEl.dispatchEvent(new Event('input', { bubbles: true }));
    };

    return (
        <span class='text-nowrap'>
            {shortcuts.map(shortcut => (
                <button
                    key={shortcut.key}
                    type='button'
                    class='btn btn-link btn-sm py-0 px-1 font-weight-lighter'
                    style='font-size: 0.75rem;'
                    data-shortcut={shortcut.key}
                    onClick={onClick}
                >
                    {shortcut.label}
                </button>
            ))}
        </span>
    );
};
